// src/hooks/useSetupWorkflow.ts
import { useState, useEffect } from 'react';
import { supabase } from '../supabase/config';
import { useAuth } from '../contexts/AuthContext';

export interface SetupStep {
  id: string;
  title: string;
  description: string;
  detailedInstructions: string;
  completed: boolean;
  category: 'essential' | 'recommended' | 'optional';
  estimatedTime: string;
  actionUrl?: string;
  actionText?: string;
  dependencies?: string[];
  helpUrl?: string;
}

export interface SetupWorkflowData {
  schoolName: string;
  lastUpdated: Date;
  completedSteps: string[];
  setupPhase: 'initial' | 'data_entry' | 'configuration' | 'testing' | 'complete';
}

const DEFAULT_SETUP_STEPS: SetupStep[] = [
  // Pre-completed by system administrator - shown as reference only
  {
    id: 'school_config',
    title: 'School Configuration ✓',
    description: 'Basic school information and academic year settings (Pre-configured)',
    detailedInstructions: 'Your school configuration has been set up by the system administrator, including school name, academic year, and term dates.',
    completed: true, // Always completed - done by you
    category: 'essential',
    estimatedTime: 'Complete',
    actionUrl: '/admin/school-settings',
    actionText: 'View Configuration',
    helpUrl: '/help/school-configuration'
  },
  {
    id: 'departments',
    title: 'Department Setup ✓',
    description: 'Academic departments created and heads assigned (Pre-configured)',
    detailedInstructions: 'All academic departments have been created by the system administrator based on your staff data. Department heads have been assigned where specified.',
    completed: true, // Always completed - done by you
    category: 'essential',
    estimatedTime: 'Complete',
    actionUrl: '/admin/school-settings',
    actionText: 'View Departments',
    helpUrl: '/help/departments'
  },
  {
    id: 'staff_data',
    title: 'Staff Data Import ✓',
    description: 'All teaching staff information imported (Pre-configured)',
    detailedInstructions: 'All teaching staff have been imported by the system administrator with their names, email addresses, departments, FTE allocations, and roles.',
    completed: true, // Always completed - done by you
    category: 'essential',
    estimatedTime: 'Complete',
    actionUrl: '/settings/user-management',
    actionText: 'View Staff',
    helpUrl: '/help/staff-import'
  },
  {
    id: 'allowance_templates',
    title: 'Teaching Load Allowance Templates',
    description: 'Configure allowances for administrative roles and special duties',
    detailedInstructions: 'Review and adjust allowance templates for different staff roles (Head of Department, Curriculum Leader, Coordinator positions, etc.). These allowances reduce teaching loads for administrative duties.',
    completed: false,
    category: 'essential',
    estimatedTime: '20 minutes',
    actionUrl: '/settings/allowance-templates',
    actionText: 'Configure Allowances',
    helpUrl: '/help/allowances'
  },
  {
    id: 'curriculum_leaders',
    title: 'Assign Curriculum Area Leaders',
    description: 'Designate staff to lead each curriculum area',
    detailedInstructions: 'Review your staff list and assign curriculum leaders for each subject area (English, Mathematics, Science, etc.). These staff members will manage teaching preferences and help with class assignments.',
    completed: false,
    category: 'essential',
    estimatedTime: '15 minutes',
    actionUrl: '/settings/user-management',
    actionText: 'Assign Curriculum Leaders',
    helpUrl: '/help/curriculum-leaders'
  },
  {
    id: 'subject_configuration',
    title: 'Review Subject Configuration', 
    description: 'Review subjects and their department mappings',
    detailedInstructions: 'Review all subjects taught at your school and verify they are correctly mapped to departments. Make any necessary adjustments for year levels, subject codes, and scheduling requirements.',
    completed: false,
    category: 'essential',
    estimatedTime: '30 minutes',
    actionUrl: '/grid/subject-config',
    actionText: 'Review Subjects',
    helpUrl: '/help/subjects'
  },
  {
    id: 'grid_import',
    title: 'Build your timetable grid',
    description: 'Create a grid and add its classes',
    detailedInstructions: 'Use the guided "Build a Grid" flow: name the grid and choose its lines, then upload your class list (with an optional line column to set which line each class runs on). You can still use Import Grid or the Classes page separately if you prefer.',
    completed: false,
    category: 'essential',
    estimatedTime: '15 minutes',
    actionUrl: '/grid/setup',
    actionText: 'Build a Grid',
    helpUrl: '/help/grid-setup'
  },
  {
    id: 'teaching_intentions',
    title: 'Collect Teaching Intentions',
    description: 'Have curriculum leaders collect staff teaching preferences',
    detailedInstructions: 'Work with your curriculum leaders to collect teaching intentions and preferences from all staff. This helps ensure teachers are assigned to subjects they prefer and are qualified to teach.',
    completed: false,
    category: 'recommended',
    estimatedTime: '1 week (ongoing)',
    actionUrl: '/intentions',
    actionText: 'Manage Intentions',
    helpUrl: '/help/intentions'
  },
  {
    id: 'teacher_allocation',
    title: 'Assign Teachers to Classes',
    description: 'Use the system tools to allocate teachers to their classes',
    detailedInstructions: 'Use the teacher allocation tools to assign staff to classes. You can use AI suggestions based on preferences and qualifications, or manually assign teachers. This is the core timetabling activity.',
    completed: false,
    category: 'essential',
    estimatedTime: '2-3 hours',
    actionUrl: '/grid/multi-editor',
    actionText: 'Allocate Teachers',
    helpUrl: '/help/allocation'
  },
  {
    id: 'load_calculation',
    title: 'Calculate Teaching Loads',
    description: 'Generate teaching load calculations and letters',
    detailedInstructions: 'Run the load calculation system to determine each staff member\'s teaching load including classes, duties, and allowances. Generate load letters for staff review and verification.',
    completed: false,
    category: 'essential',
    estimatedTime: '30 minutes',
    actionUrl: '/load-letter/generation',
    actionText: 'Calculate Loads',
    helpUrl: '/help/load-calculation'
  },
  {
    id: 'conflict_resolution',
    title: 'Resolve Timetable Conflicts',
    description: 'Address any scheduling conflicts or issues',
    detailedInstructions: 'Review the conflict resolution page to identify and resolve any scheduling conflicts, teacher overallocations, or other timetabling issues flagged by the system.',
    completed: false,
    category: 'recommended',
    estimatedTime: '30 minutes',
    actionUrl: '/grid/conflicts',
    actionText: 'Resolve Conflicts',
    helpUrl: '/help/conflicts'
  },
  {
    id: 'system_verification',
    title: 'Final System Verification',
    description: 'Review reports and verify system accuracy',
    detailedInstructions: 'Run system reports to verify allocation accuracy, check preference satisfaction rates, and ensure all staff have appropriate teaching loads. Test staff access to their timetable information.',
    completed: false,
    category: 'recommended',
    estimatedTime: '45 minutes',
    actionUrl: '/reports/preference-satisfaction',
    actionText: 'View Reports',
    helpUrl: '/help/verification'
  }
];

// Steps whose completion can be inferred from real data — so the checklist
// reflects reality instead of only what someone manually ticked. "Review"/
// "verify" steps (subject config, line scheduling, conflicts, final verification)
// stay manual, since they're admin judgement calls, not data that exists or not.
async function deriveCompletedStepIds(): Promise<string[]> {
  try {
    const head = { count: 'exact' as const, head: true };
    const [areas, staff, allowances, leaders, grids, intentions, letters] = await Promise.all([
      supabase.from('curriculum_areas').select('*', head),
      supabase.from('staff').select('*', head).eq('status', 'active'),
      supabase.from('allowance_templates').select('*', head),
      supabase.from('staff').select('*', head).eq('status', 'active').eq('role', 'curriculum_leader'),
      supabase.from('grids').select('*', head),
      supabase.from('teaching_intentions').select('*', head),
      supabase.from('load_letters').select('*', head),
    ]);
    const n = (r: { count: number | null }) => r.count || 0;
    const done: string[] = [];
    if (n(areas) > 0) done.push('departments');
    if (n(staff) > 0) done.push('staff_data');
    if (n(allowances) > 0) done.push('allowance_templates');
    if (n(leaders) > 0) done.push('curriculum_leaders');
    if (n(grids) > 0) done.push('grid_import');
    if (n(intentions) > 0) done.push('teaching_intentions');
    if (n(letters) > 0) { done.push('teacher_allocation'); done.push('load_calculation'); }
    return done;
  } catch (err) {
    console.error('Error deriving setup progress:', err);
    return [];
  }
}

export const useSetupWorkflow = () => {
  const { user } = useAuth();
  const [setupSteps, setSetupSteps] = useState<SetupStep[]>(DEFAULT_SETUP_STEPS);
  const [workflowData, setWorkflowData] = useState<SetupWorkflowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load setup workflow data from Firestore
  useEffect(() => {
    const loadWorkflowData = async () => {
      if (!user) return;

      try {
        // Load the manually-tracked workflow data (the stored ticks) from settings.
        const { data: workflowRecord, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'workflow_data')
          .single();

        let stored: SetupWorkflowData;
        if (workflowRecord && !error) {
          const data = workflowRecord.value as SetupWorkflowData;
          stored = {
            ...data,
            completedSteps: Array.isArray(data.completedSteps) ? data.completedSteps : [],
            lastUpdated: data.lastUpdated ? new Date(data.lastUpdated) : new Date(),
          };
        } else {
          // First run — seed the admin-pre-completed steps.
          stored = {
            schoolName: 'Your School',
            lastUpdated: new Date(),
            completedSteps: ['school_config', 'departments', 'staff_data'],
            setupPhase: 'data_entry',
          };
          await supabase
            .from('settings')
            .upsert({ key: 'workflow_data', value: stored, updated_at: new Date().toISOString() }, { onConflict: 'tenant_id,key' });
        }

        // Keep the stored (manual) list for write operations…
        setWorkflowData(stored);

        // …but mark a step done if the underlying data clearly exists, so the
        // checklist reflects what's actually set up rather than only what was
        // manually ticked (e.g. curriculum leaders that are already assigned).
        const derived = await deriveCompletedStepIds();
        const effective = new Set([...stored.completedSteps, ...derived]);
        setSetupSteps(DEFAULT_SETUP_STEPS.map(step => ({
          ...step,
          completed: step.completed || effective.has(step.id),
        })));
      } catch (err) {
        console.error('Error loading workflow data:', err);
        setError('Failed to load setup workflow data');
      } finally {
        setLoading(false);
      }
    };

    loadWorkflowData();
  }, [user]);

  // Mark a step as completed
  const completeStep = async (stepId: string): Promise<void> => {
    if (!workflowData) return;

    try {
      const updatedCompletedSteps = [...workflowData.completedSteps];
      if (!updatedCompletedSteps.includes(stepId)) {
        updatedCompletedSteps.push(stepId);
      }

      const updatedWorkflowData = {
        ...workflowData,
        completedSteps: updatedCompletedSteps,
        lastUpdated: new Date(),
        setupPhase: calculateSetupPhase(updatedCompletedSteps)
      };

      // Update Supabase settings table
      await supabase
        .from('settings')
        .upsert({
          key: 'workflow_data',
          value: updatedWorkflowData,
          updated_at: new Date().toISOString()
        }, { onConflict: 'tenant_id,key' });

      // Update local state
      setWorkflowData(updatedWorkflowData);
      setSetupSteps(prevSteps => prevSteps.map(step =>
        step.id === stepId ? { ...step, completed: true } : step
      ));

    } catch (err) {
      console.error('Error completing step:', err);
      throw new Error('Failed to mark step as complete');
    }
  };

  // Mark a step as incomplete
  const uncompleteStep = async (stepId: string): Promise<void> => {
    if (!workflowData) return;

    try {
      const updatedCompletedSteps = workflowData.completedSteps.filter(id => id !== stepId);
      
      const updatedWorkflowData = {
        ...workflowData,
        completedSteps: updatedCompletedSteps,
        lastUpdated: new Date(),
        setupPhase: calculateSetupPhase(updatedCompletedSteps)
      };

      // Update Supabase settings table
      await supabase
        .from('settings')
        .upsert({
          key: 'workflow_data',
          value: updatedWorkflowData,
          updated_at: new Date().toISOString()
        }, { onConflict: 'tenant_id,key' });

      // Update local state
      setWorkflowData(updatedWorkflowData);
      setSetupSteps(prevSteps => prevSteps.map(step => 
        step.id === stepId ? { ...step, completed: false } : step
      ));

    } catch (err) {
      console.error('Error uncompleting step:', err);
      throw new Error('Failed to mark step as incomplete');
    }
  };

  // Calculate setup phase based on completed steps
  const calculateSetupPhase = (completedSteps: string[]): SetupWorkflowData['setupPhase'] => {
    const essentialSteps = DEFAULT_SETUP_STEPS.filter(s => s.category === 'essential');
    const completedEssential = essentialSteps.filter(s => completedSteps.includes(s.id));
    
    if (completedSteps.length === 0) return 'initial';
    if (completedEssential.length < essentialSteps.length) return 'data_entry';
    if (completedSteps.length < DEFAULT_SETUP_STEPS.length) return 'configuration';
    return 'complete';
  };

  // Get progress statistics
  const getProgressStats = () => {
    const totalSteps = setupSteps.length;
    const completedSteps = setupSteps.filter(s => s.completed).length;
    const essentialSteps = setupSteps.filter(s => s.category === 'essential');
    const completedEssential = essentialSteps.filter(s => s.completed).length;
    
    // Use the effective (data-derived + manual) completion so the next step
    // isn't blocked by a dependency that's actually done but never ticked.
    const completedIds = new Set(setupSteps.filter(s => s.completed).map(s => s.id));

    return {
      totalSteps,
      completedSteps,
      progressPercentage: Math.round((completedSteps / totalSteps) * 100),
      essentialSteps: essentialSteps.length,
      completedEssential,
      essentialProgress: Math.round((completedEssential / essentialSteps.length) * 100),
      nextStep: setupSteps.find(s => !s.completed &&
        (!s.dependencies || s.dependencies.every(dep => completedIds.has(dep)))
      )
    };
  };

  return {
    setupSteps,
    workflowData,
    loading,
    error,
    completeStep,
    uncompleteStep,
    getProgressStats: getProgressStats()
  };
};